<template>
    <tbody>
        <tr v-if="orderedItems" v-for="row in orderedItems" class="table-row" :class="setCustomClassRow(row)">
            <td v-if="body.selection && body.selection.active">
                <checkbox @input="callEventSelection($event, row)" :checked="row.selected" :disabled="row.disabledCheckbox" :class="{ 'checkbox-disabled': row.disabledCheckbox }"></checkbox>
            </td>

            <td v-for="(value, key) in header.list" :class="setCustomClassCell(key)" @click="callEventRow(row)">
                <span v-if="(body.customCells !== undefined && body.customCells[key] !== undefined && (typeof body.customCells[key](row) == 'string' || typeof body.customCells[key](row) == 'number'))" v-html="body.customCells[key](row)"></span>
               
                <template v-else-if="
                    body.customCells &&
                    body.customCells[key] &&
                    typeof body.customCells[key](row) === 'object' &&
                    body.customCells[key](row).component"
                >
                    <component
                        :is="body.customCells[key](row).component"
                        :arguments="body.customCells[key](row).arguments"
                        v-on="body.customCells[key](row).on">
                    </component>
                </template>
                
                <span v-else-if="
                    body.customCells && 
                    body.customCells[key] && 
                    typeof body.customCells[key](row) === 'object' && 
                    (body.customCells[key](row).html || body.customCells[key](row).tooltip) && 
                    !body.customCells[key](row).component
                "
                    v-html="body.customCells[key](row).html"
                    v-tooltip="body.customCells[key](row).tooltip"
                >
                </span>
                
                <span v-else>{{ row[key] }}</span>
            </td>

            <td v-if="actions.list && actions.list.length > 0">
                <component :is="actionsTemplateSelection" :row="row" :actions="actions"></component>
            </td>
        </tr>

        <tr v-else>
            <td>Loading items ...</td>
        </tr>
    </tbody>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    props: {
        header: Object,
        body: Object,
        rows: Array,
        actions: Object,
        selected: Boolean,
    },
    computed: {
        ...mapGetters([
            'getIsCreatingInvoice',
            'getIsSavingReservation'
        ]),
        orderedItems() {
            const rows = this.rows || [];
            const sort = this.header && this.header.sort;
            if (!sort) return rows;

            // Supports: header.sort.list = [{ key, direction }, ...] (optional multi col)
            const list = Array.isArray(sort.list) ? sort.list : [];
            if (!list.length) return rows;

            const listNorm = list
                .filter(item => item && item.key)
                .map(item => ({ key: item.key, dir: (item.direction === 'desc' ? -1 : 1) }));

            if (!listNorm.length) return rows;

            // Cascading sorting
            return rows.slice().sort((a, b) => {
                for (let i = 0; i < listNorm.length; i++) {
                    const { key, dir } = listNorm[i];

                    // 1) optional per-column custom getter (if provided in the header)
                    const getterMap = sort.getters || null;
                    const getter = getterMap && typeof getterMap[key] === 'function' ? getterMap[key] : null;

                    // 2) extract the cell’s “visible text”
                    const av = getter ? getter(a) : this.cellString(a, key);
                    const bv = getter ? getter(b) : this.cellString(b, key);

                    // Empty values, if exist, go to the end
                    const aEmpty = av === '' || av == null;
                    const bEmpty = bv === '' || bv == null;
                    
                    if (aEmpty && !bEmpty) return 1;
                    if (bEmpty && !aEmpty) return -1;

                    // Compare the two cell values as human-friendly strings:
                    // - String(...) ensures both sides are strings
                    // - localeCompare with { numeric: true } does "natural" sorting (e.g., "item2" < "item10")
                    // - sensitivity: 'base' ignores case and accents for ordering
                    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
                    
                    // If they differ, apply the chosen direction (dir = +1 for ASC, -1 for DESC)
                    // so the comparator returns the correct sign for ascending/descending order.
                    if (cmp !== 0) return cmp * dir;
                }

                return 0;
            });
        },
        selectedAll() {
            return this.selected ? true : false;
        },
        actionsTemplateSelection() {
            const prop = this.actions;
            const path = (prop && prop.template !== undefined && prop.template) ? prop.template : './baseActions';

            if (path === '') {
                return;
            }

            try {
                return require(`${path}`);
            }
            catch (e) {
                console.log('Unable to find (' + path + ') for this automated list');
            }
        },
    },
    methods: {
        stripHtml(s) {
            if (s == null) return '';
            return String(s).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
        },
        toStr(v) {
            if (v == null) return '';
            return String(v).trim();
        },
        cellString(row, key) {
            const custom = this.body && this.body.customCells && this.body.customCells[key];
            if (custom) {
                const val = custom(row);
                const t = typeof val;

                if (t === 'string' || t === 'number') return this.stripHtml(String(val));
                if (t === 'object') {
                    // Tooltips path { html, tooltip }
                    if (val && (val.html || val.tooltip)) return this.stripHtml(val.html || '');
                }
            }

            return this.stripHtml(this.toStr(row[key]));
        },
        callEventSelection(event, row) {
            if (!row.disabledCheckbox && this.body.selection.event) {
                this.body.selection.event(event, row);
            }
        },
        callEventRow(row) {
            if (this.body.customRowEvent) {
                // If a reservation or an invoice is being saved, don't let the user open other reservation
                // Why this solution? While saving a reservation, if the user close the current reservation and try to open an other, this other reservation open with data of the first reservation
                if (this.getIsCreatingInvoice || this.getIsSavingReservation === true) {
                    if (this.getIsSavingReservation === true) {
                        this.$toasted.show('Your reservation is being processed in the background. Please try again shortly.', { theme: "bubble" });
                    }

                    if (this.getIsCreatingInvoice) {
                        this.$toasted.show('Your reservation is being processed in the background. Please try again shortly.', { theme: "bubble" });
                    }
                } else {
                    this.body.customRowEvent(row);
                }
            }
        },
        setCustomClassRow(row) {
            return this.body.customRowClass ? this.body.customRowClass(row) : '';
        },
        setCustomClassCell(key) {
            return (this.body.customClassCell) ? this.body.customClassCell[key] : '';
        }
    }
}
</script>

<style lang="scss" scoped>
    .table {
        tbody tr:hover {
            border: none;
            transform: none;
        }

        // Reservation list status row backgrounds
        tr.row-status-green td { background-color: rgba(76,175,80,0.12); }
        tr.row-status-red td { background-color: rgba(244,67,54,0.08); }
        tr.row-status-yellow td { background-color: rgba(255,193,7,0.08); }
        tr.row-status-grey td { background-color: rgba(120,120,120,0.12); }

        tr.row-status-green:hover td { background-color: rgba(76,175,80,0.09); }
        tr.row-status-red:hover td { background-color: rgba(244,67,54,0.09); }
        tr.row-status-yellow:hover td { background-color: rgba(255,193,7,0.10); }
        tr.row-status-grey:hover td { background-color: rgba(120,120,120,0.16); }
    }

    .checkbox-disabled {
        opacity: 0.4;
        cursor: not-allowed !important;

        &:hover {
            opacity: 0.4;
        }

        &:disabled {
            opacity: 0.4;
            cursor: not-allowed !important;
        }
    }
</style>


// WEBPACK FOOTER //
// baseBody.vue?23db6995